import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { User } from '@/api/client/models/index.ts';
import { useCurrentUserStore } from '@/stores/current-user-store.ts';
import { useCurrentAppStore } from '@/stores/current-app-store.ts';
import { AllotmentRolesConstants } from '@/stores/definitions.js';
import { useIataStore } from '@/stores/iata.ts';
import { useBrandingsStore } from '@/stores/reference-data/brandings.ts';
import { useFareBasisCodesStore } from '@/stores/reference-data/fare-basis-code.ts';
import { useOperatingAirlinesStore } from '@/stores/reference-data/operating-airlines.ts';
import { useSeasonsStore } from '@/stores/reference-data/seasons.ts';
import { useTourOperatorsStore } from '@/stores/reference-data/tour-operators.ts';
import {useAllotmentsStore} from "@/stores/allotments/allotment-store.ts";
import { useCharterAircraftTypesStore } from '@/charter/stores/charter-aircraft-types-store.ts';
import { useAcmiStore } from '@/charter/stores/charter-acmi-store.ts';
import { useCharterClientsStore } from '@/charter/stores/charter-clients-store.ts';
import { useCharterFlightNumbersStore } from '@/charter/stores/charter-flight-numbers-store.ts';
import { useCharterRevVardocStore } from '@/charter/stores/charter-rev-vardoc-store.ts';
import { useCharterRoleStore } from '@/charter/stores/charter-role-store.ts';
import { useCharterSeasonsStore } from '@/charter/stores/charter-seasons-store.ts';

type ReferenceDataRequest = Promise<unknown>;

function getAllotmentRoleId(user: User | null): number | null {
  const roleId = user?.allotmentRoleId;

  if (roleId === null || roleId === undefined) {
    return null;
  }

  return Number(roleId);
}

export const useRootStore = defineStore('root', () => {
  const allotmentDataLoaded = ref<boolean>(false);
  const charterDataLoaded = ref<boolean>(false);
  const dataLoaded = computed<boolean>(() => allotmentDataLoaded.value || charterDataLoaded.value);

  const userStore = useCurrentUserStore();
  const currentAppStore = useCurrentAppStore();
  const tourOperatorsStore = useTourOperatorsStore();
  const brandingsStore = useBrandingsStore();
  const fareBasisCodesStore = useFareBasisCodesStore();
  const iataStore = useIataStore();
  const operatingAirlinesStore = useOperatingAirlinesStore();
  const seasonsStore = useSeasonsStore();
  const allotmentsStore = useAllotmentsStore();
  const charterAircraftTypesStore = useCharterAircraftTypesStore();
  const charterAcmiStore = useAcmiStore();
  const charterClientsStore = useCharterClientsStore();
  const charterFlightNumbersStore = useCharterFlightNumbersStore();
  const charterRevVardocStore = useCharterRevVardocStore();
  const charterRoleStore = useCharterRoleStore();
  const charterSeasonsStore = useCharterSeasonsStore();

  async function loadAllotmentReferenceData(): Promise<void> {
    if (allotmentDataLoaded.value) {
      return;
    }

    const currentUser = userStore.currentUser;
    const roleId = getAllotmentRoleId(currentUser);

    const adminServices: ReferenceDataRequest[] = roleId === AllotmentRolesConstants.ADMIN
        || roleId === AllotmentRolesConstants.READONLY_ADMIN
        ? [
            tourOperatorsStore.getAbstract(),
            tourOperatorsStore.getRoles(),
            allotmentsStore.getRoles(),
            operatingAirlinesStore.get({ showOnlyActive: true }),
            seasonsStore.getAbstract(),
            iataStore.get(),
            fareBasisCodesStore.get({ showOnlyActive: true }),
            brandingsStore.get({ showOnlyActive: true }),
          ]
        : [];

    const hccReadonlyServices: ReferenceDataRequest[] = roleId === AllotmentRolesConstants.HCC_READONLY
        ? [
            tourOperatorsStore.getAbstract(),
            operatingAirlinesStore.get({ showOnlyActive: true }),
            seasonsStore.getAbstract(),
            iataStore.get(),
            fareBasisCodesStore.get({ showOnlyActive: true }),
            brandingsStore.get({ showOnlyActive: true }),
          ]
        : [];

    const tourOperatorServices: ReferenceDataRequest[] = roleId === AllotmentRolesConstants.TOUR_OPERATOR
        ? [
            seasonsStore.getAbstract(),
            iataStore.get(),
            fareBasisCodesStore.get({ showOnlyActive: true }),
            brandingsStore.get({ showOnlyActive: true }),
          ]
        : [];

    const financeServices: ReferenceDataRequest[] = roleId === AllotmentRolesConstants.FINANCE
        ? [
            tourOperatorsStore.getAbstract(),
          ]
        : [];

    await Promise.all([
      ...adminServices,
      ...hccReadonlyServices,
      ...tourOperatorServices,
      ...financeServices,
    ]);

    allotmentDataLoaded.value = true;
  }

  async function loadCharterReferenceData(): Promise<void> {
    if (charterDataLoaded.value) {
      return;
    }

    if (!userStore.currentUser?.charterRoleId) {
      return;
    }

    await Promise.all([
      charterAircraftTypesStore.fetchAircraftTypes(),
      charterAcmiStore.fetchAcmi(),
      charterClientsStore.fetchClients(),
      charterFlightNumbersStore.fetchFlightNumbers(),
      charterRevVardocStore.fetchRevVardocs(),
      charterRoleStore.fetchRoles(),
      charterSeasonsStore.fetchSeasons(),
    ]);

    charterDataLoaded.value = true;
  }

  async function loadReferenceData(): Promise<void> {
    if (currentAppStore.isCharterApp) {
      await loadCharterReferenceData();
      return;
    }

    await loadAllotmentReferenceData();
  }

  return {
    dataLoaded,
    loadReferenceData,
    loadAllotmentReferenceData,
    loadCharterReferenceData,
  };
});
